// src/theme.js
import { createTheme } from "@mui/material/styles";

const PRIMARY_NAVY = "#003366"; // main brand blue
const PRIMARY_LIGHT = "#1B4F8F"; // lighter/nav hover
const PRIMARY_SOFT = "#E4EDF7"; // soft tint for hovers / backgrounds
const ACCENT_BLUE = "#2196F3"; // accent (links, chips, highlights)

const BG_APP = "#f8f9fa"; // app background
const BG_CARD = "#ffffff"; // paper / cards
const BORDER_SOFT = "#e0e4ea"; // subtle borders

const TEXT_MAIN = "#212529";
const TEXT_MUTED = "#6c757d";

const theme = createTheme({
  palette: {
    mode: "light",

    primary: {
      main: PRIMARY_NAVY,
      light: PRIMARY_LIGHT,
      dark: "#001b3d",
      contrastText: "#ffffff",
    },

    secondary: {
      main: ACCENT_BLUE,
      light: "#64b5f6",
      dark: "#1976d2",
      contrastText: "#ffffff",
    },

    background: {
      default: BG_APP,
      paper: BG_CARD,
    },

    text: {
      primary: TEXT_MAIN,
      secondary: TEXT_MUTED,
      disabled: "#adb5bd",
    },

    divider: BORDER_SOFT,

    error: { main: "#D32F2F" },
    warning: { main: "#ED6C02" },
    info: { main: "#0288D1" },
    success: { main: "#2E7D32" },
  },

  shape: {
    borderRadius: 8,
  },

  typography: {
    fontFamily: `'Roboto', 'Helvetica', 'Arial', sans-serif`,

    h1: {
      fontSize: "2rem",
      fontWeight: 600,
      letterSpacing: 0.2,
      color: PRIMARY_NAVY,
    },
    h2: {
      fontSize: "1.7rem",
      fontWeight: 600,
      letterSpacing: 0.15,
      color: PRIMARY_NAVY,
    },
    h3: {
      fontSize: "1.4rem",
      fontWeight: 600,
      letterSpacing: 0.1,
      color: PRIMARY_NAVY,
    },
    h4: {
      fontSize: "1.2rem",
      fontWeight: 500,
      letterSpacing: 0.05,
      color: PRIMARY_NAVY,
    },
    h5: {
      fontSize: "1rem",
      fontWeight: 500,
      color: TEXT_MAIN,
    },
    h6: {
      fontSize: "0.95rem",
      fontWeight: 500,
      color: TEXT_MAIN,
    },

    body1: {
      fontSize: "0.9rem",
      color: TEXT_MAIN,
      lineHeight: 1.6,
    },
    body2: {
      fontSize: "0.85rem",
      color: TEXT_MUTED,
      lineHeight: 1.5,
    },

    button: {
      textTransform: "none",
      fontWeight: 500,
      fontSize: "0.85rem",
      letterSpacing: 0.2,
    },

    caption: {
      fontSize: "0.75rem",
      color: TEXT_MUTED,
    },

    overline: {
      fontSize: "0.7rem",
      textTransform: "uppercase",
      letterSpacing: 1,
      color: TEXT_MUTED,
    },
  },

  components: {
    // AppBar – navy, flat, compact
    MuiAppBar: {
      defaultProps: {
        color: "primary",
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundColor: PRIMARY_NAVY,
          minHeight: 56,
        },
      },
    },

    // Buttons – rounded pills, subtle hover
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 999,
          textTransform: "none",
          fontWeight: 500,
          paddingInline: 18,
        },
        containedPrimary: {
          backgroundColor: PRIMARY_NAVY,
          "&:hover": {
            backgroundColor: PRIMARY_LIGHT,
          },
        },
        outlinedPrimary: {
          borderColor: PRIMARY_NAVY,
          color: PRIMARY_NAVY,
          backgroundColor: "#ffffff",
          "&:hover": {
            borderColor: PRIMARY_LIGHT,
            backgroundColor: PRIMARY_SOFT,
          },
        },
      },
    },

    // Cards – subtle border & shadow (your custom screens override if needed)
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${BORDER_SOFT}`,
          boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
          backgroundColor: BG_CARD,
        },
      },
    },

    // Paper (dialogs, menus, etc.)
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },

    // TextFields – small, compact, with soft borders
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "small",
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: PRIMARY_LIGHT,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: PRIMARY_NAVY,
          },
        },
        notchedOutline: {
          borderColor: BORDER_SOFT,
        },
        input: {
          fontSize: "0.85rem",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "0.8rem",
          color: TEXT_MUTED,
        },
      },
    },

    // Tabs – pill-like selected tab
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 44,
        },
        indicator: {
          backgroundColor: "transparent",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          fontSize: "0.85rem",
          minHeight: 44,
          padding: "6px 16px",
          color: TEXT_MUTED,
          borderRadius: 999,
          "&.Mui-selected": {
            backgroundColor: PRIMARY_SOFT,
            color: PRIMARY_NAVY,
          },
        },
      },
    },

    // Tables – light grey header, compact rows
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: "#edf2f7",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          color: PRIMARY_NAVY,
          fontSize: "0.8rem",
        },
        body: {
          fontSize: "0.8rem",
          color: TEXT_MAIN,
        },
      },
    },

    // Chips – for statuses / tags
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontSize: "0.75rem",
          fontWeight: 500,
        },
        filledPrimary: {
          backgroundColor: PRIMARY_LIGHT,
        },
        outlined: {
          borderColor: BORDER_SOFT,
        },
      },
    },

    // Links – clean, blue, with hover underline
    MuiLink: {
      styleOverrides: {
        root: {
          color: ACCENT_BLUE,
          textDecoration: "none",
          "&:hover": {
            textDecoration: "underline",
          },
        },
      },
    },
  },
});

export default theme;
